import PropType from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { ButtonAppearance } from '../../Atoms/Button';

import { Color } from '../../../shared/constants/Colors';
import * as S from './DropdownMenu.styles';

export const DropdownMenu = ({ title, ariaLabel, children }) => {
  const [isOpen, setIsOpen] = useState(false);

  const dropdownRef = useRef(null);

  /**
   * This effect is used to handle the click and keydown events outside of the dropdown menu.
   * When the dropdown is open, it listens for clicks outside the dropdown to close it.
   * It also listens for the Escape key to close the dropdown.
   * This is to ensure that the dropdown menu can be closed by clicking outside of it or pressing the Escape key.
   * It also handles focus out events to close the dropdown when focus leaves the dropdown.
   */
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    const handleFocusOut = event => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.relatedTarget)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
      dropdownRef.current?.addEventListener('focusout', handleFocusOut);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
      dropdownRef.current?.removeEventListener('focusout', handleFocusOut);
    };
  }, [isOpen]);

  /**
   * This effect is used to manage the focus of the dropdown menu items.
   * When the dropdown is open, it sets the tabindex and aria-hidden attributes
   * to the focusable elements inside the dropdown panel.
   * When the dropdown is closed, it sets tabindex to -1 and aria-hidden to true.
   * This is to ensure that only the dropdown items are focusable when the dropdown is open.
   */
  useEffect(() => {
    const panel = dropdownRef.current?.querySelector('[role="listbox"]');
    if (panel) {
      const focusables = panel.querySelectorAll(
        'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
      );
      focusables.forEach(el => {
        if (!isOpen) {
          el.setAttribute('tabindex', '-1');
          el.setAttribute('aria-hidden', 'true');
        } else {
          el.removeAttribute('tabindex');
          el.removeAttribute('aria-hidden');
        }
      });
    }
  }, [isOpen]);

  const menuId = `${title?.replace(/\s+/g, '-')}-menu`;

  return (
    <S.DropdownContainer ref={dropdownRef}>
      <S.ButtonWrapper>
        <S.StyledButton
          isOpen={isOpen}
          isIconOnRight={true}
          iconColor={Color.CLOUD_BURST}
          appearance={ButtonAppearance.MINIMAL}
          onClick={() => setIsOpen(prev => !prev)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsOpen(prev => !prev);
            }
          }}
          aria-haspopup='listbox'
          aria-controls={menuId}
          aria-expanded={isOpen}
          aria-label={ariaLabel}
          tabIndex={0}
        >
          <S.AngleIcon isOpen={isOpen} />
          {title}
        </S.StyledButton>

        <S.DropdownPanel
          isVisible={isOpen}
          role='listbox'
          id={menuId}
          aria-hidden={!isOpen}
        >
          <S.ContentWrapper aria-hidden={!isOpen} isVisible={isOpen}>
            {children}
          </S.ContentWrapper>
        </S.DropdownPanel>
      </S.ButtonWrapper>
    </S.DropdownContainer>
  );
};

DropdownMenu.propTypes = {
  ariaLabel: PropType.string,
  title: PropType.string,
  children: PropType.node,
};
